/**
 * walkthrough-voice-d4.js — Mic-first capture for Walkthrough Voice (D4 Edge tool).
 * Fills #opf_wv-recording and enables #opGenBtn when a transcript is ready.
 * Soft pre-check: detect blocked mic and surface paste / unblock path before tap.
 */
(function () {
  'use strict';

  var SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

  var state = 'idle';
  var recognition = null;
  var mediaRecorder = null;
  var audioChunks = [];
  var audioStream = null;
  var timerId = null;
  var elapsed = 0;
  var finalTranscript = '';
  var micPermission = 'unknown'; /* granted | denied | prompt | unknown */

  function $(id) { return document.getElementById(id); }

  function apiVoiceUrl(path) {
    var edge = window.OporaEdge || {};
    if (typeof edge.apiUrl === 'function') return edge.apiUrl(path);
    var base = (window.OPORA_WORKER_URL || 'https://api.oporasupply.com').replace(/\/$/, '');
    return base + (path.charAt(0) === '/' ? path : '/' + path);
  }

  function setState(next) {
    state = next;
    var idle = $('wv-d4-idle');
    var recording = $('wv-d4-recording');
    var processing = $('wv-d4-processing');
    var ready = $('wv-d4-ready');
    var genBtn = $('opGenBtn');

    if (idle) idle.hidden = next !== 'idle';
    if (recording) recording.hidden = next !== 'recording';
    if (processing) processing.hidden = next !== 'processing';
    if (ready) ready.hidden = next !== 'ready';

    if (genBtn) {
      genBtn.disabled = next !== 'ready' || !finalTranscript.trim();
    }
  }

  function formatTime(secs) {
    var m = String(Math.floor(secs / 60)).padStart(2, '0');
    var s = String(secs % 60).padStart(2, '0');
    return m + ':' + s;
  }

  function startTimer() {
    elapsed = 0;
    var el = $('wv-d4-timer');
    if (el) el.textContent = formatTime(0);
    stopTimer();
    timerId = setInterval(function () {
      elapsed++;
      if (el) el.textContent = formatTime(elapsed);
    }, 1000);
  }

  function stopTimer() {
    if (timerId) {
      clearInterval(timerId);
      timerId = null;
    }
  }

  function showError(msg) {
    var el = $('wv-d4-error');
    if (!el) return;
    el.textContent = msg;
    el.hidden = false;
  }

  function clearError() {
    var el = $('wv-d4-error');
    if (el) {
      el.textContent = '';
      el.hidden = true;
    }
  }

  function goPasteInstead() {
    var toggle = $('d4PasteToggle');
    if (toggle) {
      toggle.click();
      return;
    }
    var convo = $('d4ConvoZone');
    var form = $('d4FormZone');
    if (form) form.hidden = true;
    if (convo) {
      convo.hidden = false;
      var ta = $('opConvoInput');
      if (ta) ta.focus();
    }
  }

  function applyMicUi(status) {
    micPermission = status || 'unknown';
    var panel = $('wv-d4-mic-status');
    var title = $('wv-d4-mic-status-title');
    var body = $('wv-d4-mic-status-body');
    var idle = $('wv-d4-idle');
    var idleTitle = $('wv-d4-idle-title');
    var idleHint = $('wv-d4-idle-hint');
    var recheck = $('wv-d4-mic-recheck');
    var pasteBtn = $('wv-d4-paste-fallback');

    if (!panel) return;

    if (status === 'denied') {
      panel.hidden = false;
      panel.setAttribute('data-state', 'denied');
      if (title) title.textContent = 'Microphone is blocked for this site';
      if (body) {
        body.textContent = 'In the address bar, open site settings → Microphone → Allow, then refresh. Or paste your walkthrough notes — same scope output.';
      }
      if (recheck) recheck.hidden = false;
      if (pasteBtn) pasteBtn.hidden = false;
      if (idle) idle.classList.add('is-mic-blocked');
      if (idleTitle) idleTitle.textContent = 'Mic blocked — paste works now';
      if (idleHint) {
        idleHint.textContent = 'Voice needs permission in your browser settings. Paste notes below for the same structured scope.';
      }
      return;
    }

    if (status === 'granted') {
      panel.hidden = true;
      panel.setAttribute('data-state', 'granted');
      if (idle) idle.classList.remove('is-mic-blocked');
      if (idleTitle) idleTitle.textContent = 'Tap to start narrating';
      if (idleHint) {
        idleHint.textContent = 'Mic ready. Walk the property and describe each room, surface, and frequency.';
      }
      clearError();
      return;
    }

    if (status === 'prompt') {
      panel.hidden = true;
      panel.setAttribute('data-state', 'prompt');
      if (idle) idle.classList.remove('is-mic-blocked');
      if (idleTitle) idleTitle.textContent = 'Tap to start narrating';
      if (idleHint) {
        idleHint.textContent = 'Walk the property and describe each room, surface, and frequency. Your browser will ask to use the mic once.';
      }
      return;
    }

    panel.hidden = true;
    panel.setAttribute('data-state', '');
    if (idle) idle.classList.remove('is-mic-blocked');
  }

  function checkMicPermission() {
    if (!navigator.permissions || !navigator.permissions.query) {
      applyMicUi('unknown');
      return Promise.resolve('unknown');
    }
    try {
      return navigator.permissions.query({ name: 'microphone' }).then(function (result) {
        applyMicUi(result.state);
        result.onchange = function () {
          applyMicUi(result.state);
        };
        return result.state;
      }).catch(function () {
        applyMicUi('unknown');
        return 'unknown';
      });
    } catch (e) {
      applyMicUi('unknown');
      return Promise.resolve('unknown');
    }
  }

  function commitTranscript(text) {
    finalTranscript = (text || '').trim();
    var field = $('opf_wv-recording');
    var preview = $('wv-d4-transcript-preview');
    if (field) field.value = finalTranscript;
    if (preview) preview.value = finalTranscript;
    setState('ready');
    clearError();
  }

  function stopStreams() {
    if (audioStream) {
      audioStream.getTracks().forEach(function (t) { t.stop(); });
      audioStream = null;
    }
    if (recognition) {
      try { recognition.stop(); } catch (e) {}
      recognition = null;
    }
  }

  function handleMicDenied() {
    applyMicUi('denied');
    showError('Microphone access denied. Allow the mic in your browser settings, or paste notes instead.');
    setState('idle');
    stopTimer();
  }

  /* ── Speech recognition (preferred — no upload) ── */
  function startSpeechCapture() {
    if (!SpeechRecognition) {
      startAudioCapture();
      return;
    }

    clearError();
    recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';

    var interim = '';
    var finals = [];

    recognition.onresult = function (e) {
      interim = '';
      for (var i = e.resultIndex; i < e.results.length; i++) {
        var chunk = e.results[i][0].transcript;
        if (e.results[i].isFinal) finals.push(chunk);
        else interim += chunk;
      }
      var live = $('wv-d4-live-text');
      if (live) live.textContent = finals.join(' ') + (interim ? ' ' + interim : '');
    };

    recognition.onerror = function (e) {
      if (e.error === 'not-allowed') {
        handleMicDenied();
        return;
      }
      if (e.error !== 'aborted' && e.error !== 'no-speech') {
        showError('Voice capture interrupted. Try again or paste your notes.');
      }
    };

    recognition.onend = function () {
      if (state !== 'recording') return;
      var text = finals.join(' ').trim();
      if (!text) {
        showError('No speech detected. Tap the mic and narrate as you walk, or paste notes instead.');
        setState('idle');
        stopTimer();
        return;
      }
      stopTimer();
      commitTranscript(text);
    };

    try {
      recognition.start();
      setState('recording');
      startTimer();
      var live = $('wv-d4-live-text');
      if (live) live.textContent = 'Listening… narrate room by room.';
    } catch (err) {
      startAudioCapture();
    }
  }

  function stopSpeechCapture() {
    if (recognition) {
      try { recognition.stop(); } catch (e) {}
    } else {
      stopAudioCapture();
    }
  }

  /* ── MediaRecorder fallback → worker transcribe ── */
  function startAudioCapture() {
    clearError();
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      showError('Recording is not supported in this browser. Use “Paste notes instead” below.');
      return;
    }

    navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true }
    }).then(function (stream) {
      applyMicUi('granted');
      audioStream = stream;
      audioChunks = [];
      var mime = '';
      ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg'].some(function (t) {
        if (MediaRecorder.isTypeSupported(t)) { mime = t; return true; }
        return false;
      });
      try {
        mediaRecorder = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      } catch (e) {
        mediaRecorder = new MediaRecorder(stream);
      }
      mediaRecorder.ondataavailable = function (ev) {
        if (ev.data && ev.data.size) audioChunks.push(ev.data);
      };
      mediaRecorder.onstop = onAudioStop;
      mediaRecorder.start(400);
      setState('recording');
      startTimer();
      var live = $('wv-d4-live-text');
      if (live) live.textContent = 'Recording audio…';
    }).catch(function () {
      handleMicDenied();
    });
  }

  function stopAudioCapture() {
    stopTimer();
    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      mediaRecorder.stop();
    } else {
      onAudioStop();
    }
    stopStreams();
  }

  function onAudioStop() {
    if (!audioChunks.length) {
      showError('No audio captured. Try again or paste your notes.');
      setState('idle');
      return;
    }
    setState('processing');
    var type = (mediaRecorder && mediaRecorder.mimeType) || 'audio/webm';
    var blob = new Blob(audioChunks, { type: type });
    audioChunks = [];
    mediaRecorder = null;

    var form = new FormData();
    form.append('audio', blob, 'walkthrough.webm');
    form.append('content_type', type);

    fetch(apiVoiceUrl('/api/voice/transcribe'), { method: 'POST', body: form })
      .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, data: d }; }); })
      .then(function (res) {
        if (res.ok && res.data && res.data.transcript) {
          commitTranscript(res.data.transcript);
          return;
        }
        showError('Transcription unavailable. Paste your walkthrough notes instead.');
        setState('idle');
      })
      .catch(function () {
        showError('Could not transcribe audio. Paste your walkthrough notes instead.');
        setState('idle');
      });
  }

  function onStart() {
    if (state === 'recording') return;
    if (micPermission === 'denied') {
      checkMicPermission().then(function (status) {
        if (status === 'denied') {
          handleMicDenied();
          return;
        }
        finalTranscript = '';
        var field = $('opf_wv-recording');
        if (field) field.value = '';
        startSpeechCapture();
      });
      return;
    }
    finalTranscript = '';
    var field = $('opf_wv-recording');
    if (field) field.value = '';
    startSpeechCapture();
  }

  function onStop() {
    if (state !== 'recording') return;
    if (recognition) stopSpeechCapture();
    else stopAudioCapture();
  }

  function bind() {
    var start = $('wv-d4-start');
    var stop = $('wv-d4-stop');
    var edit = $('wv-d4-edit-transcript');
    var preview = $('wv-d4-transcript-preview');
    var pasteFallback = $('wv-d4-paste-fallback');
    var recheck = $('wv-d4-mic-recheck');

    if (start) start.addEventListener('click', onStart);
    if (stop) stop.addEventListener('click', onStop);

    if (pasteFallback) {
      pasteFallback.addEventListener('click', function () {
        goPasteInstead();
      });
    }

    if (recheck) {
      recheck.addEventListener('click', function () {
        checkMicPermission().then(function (status) {
          if (status === 'granted') {
            clearError();
            applyMicUi('granted');
            return;
          }
          if (status === 'denied') {
            handleMicDenied();
            return;
          }
          applyMicUi(status);
          clearError();
          showError('Tap the mic to allow access, or paste notes instead.');
        });
      });
    }

    if (preview) {
      preview.addEventListener('input', function () {
        finalTranscript = preview.value.trim();
        var field = $('opf_wv-recording');
        if (field) field.value = finalTranscript;
        var genBtn = $('opGenBtn');
        if (genBtn) genBtn.disabled = !finalTranscript;
      });
    }

    if (edit) {
      edit.addEventListener('click', function () {
        if (preview) {
          preview.removeAttribute('readonly');
          preview.focus();
        }
      });
    }

    setState('idle');
    checkMicPermission();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bind);
  } else {
    bind();
  }
}());
